//React
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
//Styling
import "./picklist.scss";
//MUI
import { Box, Button, TextField } from "@mui/material";
import Modal from "@mui/material/Modal";

//Components
import MainAlert from "../reusables/MainAlert";
import Loader from "../reusables/Loader";

//Utils
import { handleAlert } from "../../utils/alert";
import {
  getPickListById,
  ShowMergedPicklists,
  updatePicklList,
} from "../../utils/api";
import { axiosInstance } from "../../utils/axiosInstance";

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: 300,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "8px",
  p: 4,
};

export default function AdminPickListSplit() {
  //Main
  const [user, setUser] = useState<any>({});
  const navigate = useNavigate();
  //Params
  const { id = "1" } = useParams();
  //Picklist
  const [picklist, setPicklist] = useState<any>({});
  const [productList, setProductList] = useState<any>([]);

  //Alert
  const [alert, setAlert] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [severity, setSeverity] = useState<any>("");
  const [loader, setLoader] = useState<boolean>(true);
  //Modals
  const [confirm, setConfirm] = useState<boolean>(false);
  const [confirmMessage, setConfirmMessage] = useState<string>("");
  const [showBoxes, setShowBoxes] = useState<boolean>(false);
  const [showCases, setShowCases] = useState<boolean>(false);

  const handleSelect = (productId: number) => {
    setProductList((prev: any) => {
      if (!prev) return prev;
      const updatedProducts = prev.map((p: any) => {
        if (p.productId === productId) {
          return {
            ...p,
            toSplit: !p.toSplit,
          };
        }
        return p;
      });

      return updatedProducts;
    });
  };

  const handleQtyChange = (productId: string, inputValue: string) => {
    const rawQty = inputValue === "" ? 0 : Number(inputValue);

    setProductList((prev: any) => {
      if (!prev) return prev;

      return prev.map((p: any) => {
        if (p.productId === productId) {
          const maxQty = p.qtyOrdered - p.picked;
          const qty = Math.max(0, Math.min(rawQty, maxQty));

          return {
            ...p,
            splitQty: isNaN(qty) ? 0 : qty,
          };
        }
        return p;
      });
    });
  };

  useEffect(() => {
    setLoader(true);
    //Bring in User
    let userDetails = localStorage.getItem("userDetails");
    if (userDetails) {
      let parsedUserDetails = JSON.parse(userDetails);
      setUser(parsedUserDetails);
    }
    //Bring in Picklist
    LoadPicklist();
    setLoader(false);
  }, []);

  const LoadPicklist = async () => {
    try {
      const res = await getPickListById(id);

      if (res === "error" || !res || !res.products) {
        setMessage("Unable to load picklist");
        setAlert(true);
        setPicklist(null); // Reset state
        setProductList([]);
        return;
      }

      setPicklist(res);
      setProductList(
        res.products.map((product: any) => ({
          ...product,
          toSplit: false,
          splitQty: product.qtyOrdered - product.picked,
        }))
      );
    } catch (error) {
      console.error("Picklist load failed:", error);
      setMessage("Failed to fetch picklist");
      setAlert(true);
      setPicklist(null);
      setProductList([]);
    }
  };

  const handleSplit = async () => {
    const productsToSplit = productList.filter(
      (p: any) => p.toSplit && p.splitQty > 0
    );
    if (
      productList.some(
        (p: any) => p.toSplit && p.splitQty > p.qtyOrdered - p.picked
      )
    ) {
      handleAlert(
        "Split quantity cannot exceed available quantity",
        "error",
        setMessage,
        setSeverity,
        setAlert
      );
      return;
    }

    if (productsToSplit.length === 0) {
      handleAlert(
        "Please select products with positive quantity to split",
        "error",
        setMessage,
        setSeverity,
        setAlert
      );
      return;
    }
    setLoader(true);

    const payload = {
      user: user.user,
      originalPicklistId: picklist.plHeadId,
      products: productsToSplit.map((p: any) => ({
        productId: p.productId,
        plItemId: p.plItemId,
        splitQty: p.splitQty,
      })),
    };

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_NITROPOS}/picklist/split`,
        payload
      );
      if (response.status === 201) {
        handleAlert(
          "Picklist split successfully",
          "success",
          setMessage,
          setSeverity,
          setAlert
        );
        setTimeout(() => {
          navigate(`/admin-pick-list`);
        }, 2000);
      } else {
        handleAlert(
          "Error splitting picklist",
          "error",
          setMessage,
          setSeverity,
          setAlert
        );
        setLoader(false);
      }
    } catch (error) {
      handleAlert(
        "Error splitting picklist",
        "error",
        setMessage,
        setSeverity,
        setAlert
      );
      setLoader(false);
    }
  };

  return (
    <main className="picklist">
      <h1 className="main_heading">Warehouse Picklists</h1>{" "}
      {!loader ? (
        <>
          {picklist ? (
            <>
              <h2>Picklist split section</h2>
              <h3>{`${picklist?.toLocationName} - ${picklist?.supplierName}`}</h3>
              <table className="table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Ordered Quantity (Available)</th>
                    <th>Quantity To Split</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {productList?.length > 0 ? (
                    productList.map((product: any, i: any) => {
                      return (
                        <tr
                          style={{
                            backgroundColor:
                              product.saved 
                                ? product.toSplit
                                  ? "#66BB6A"
                                  : "#c1ddf1ff"
                                : product.toSplit
                                ? "#66BB6A"
                                : "",
                          }}
                        >
                          <td>{product.name}</td>
                          <td>{`${product.qtyOrdered} (${
                            product.qtyOrdered - product.picked
                          })`}</td>
                          <td>
                            <TextField
                              type="number"
                              sx={{ width: "100px" }}
                              value={product.splitQty || 0}
                              onChange={(e) =>
                                handleQtyChange(
                                  product.productId,
                                  e.target.value
                                )
                              }
                              inputProps={{
                                min: 0,
                                max: product.qtyOrdered - product.picked,
                              }}
                            />
                          </td>
                          <td>
                            <Button
                              variant="contained"
                              disabled={
                                product.picked >= product.qtyOrdered
                              }
                              onClick={() => {
                                handleSelect(product.productId);
                              }}
                            >
                              {product.toSplit ? "Deselect" : "Select"}
                            </Button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <h1 className="empty_table_heading">
                      No products in picklist
                    </h1>
                  )}
                </tbody>
              </table>
              <Button
                variant="contained"
                color="success"
                onClick={() => {
                  handleSplit();
                }}
              >
                Confirm Split
              </Button>
            </>
          ) : (
            <h1 className="empty_table_heading">
              {" "}
              There are no picklist orders
            </h1>
          )}
        </>
      ) : (
        <Loader />
      )}
      <Modal open={alert}>
        <MainAlert message={message} severity={severity} closer={setAlert} />
      </Modal>
    </main>
  );
}
